import { Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import classNames from 'classnames';
import { uniq } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NOTICE_ADMIN_ROLE } from '.';
import { addSupportRole, editSupportRole } from '../../../../api/admin/rbac';
import { ConsoleDrawer } from '../../../../components/drawer';
import { ConsoleForm } from '../../../../components/form';
import { messageError } from '../../../../components/message';
import { CommonSelect } from '../../../../components/select';
import { CommonTag } from '../../../../components/tag';
import getFormatMessage from '../../../../locales/get-format-message';
import { RbacRoleType } from '../../../../model';
import {
  IApiAdminRbacRole,
  ISupportUserModel,
  IUserListItem,
  RbacAdminPermObjectKey,
} from '../../../../model/api-modules';
import { IRootState } from '../../../../store/reducers';
import { washRoleUsers } from '../lib';
import { SupportAccessRights } from './access-rights';
import styles from './index.module.scss';

interface IRoleFormInfo {
  name: string;
  desc: string;
  permissions?: RbacAdminPermObjectKey[];
  users: string[];
}

interface IProps {
  readonly members: readonly ISupportUserModel[];
  readonly owner: IApiAdminRbacRole;
  readonly admin: IApiAdminRbacRole;
  readonly commonRoles: readonly IApiAdminRbacRole[];
  readonly commonUsers: readonly string[];
  readonly role?: IApiAdminRbacRole;
  readonly allUser: readonly IUserListItem[];
  readonly viewOnly?: boolean;
  readonly onSave: () => void;
  readonly onCancel: () => void;
}

export const EditRoleDrawer = (props: IProps) => {
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const {
    commonUsers,
    owner,
    admin,
    role,
    allUser,
    onSave,
    onCancel,
    viewOnly,
  } = props;
  const [form] = useForm<IRoleFormInfo>();

  const isNoticeAdminRole = role?.roleName === NOTICE_ADMIN_ROLE;

  const getUserOptions = () => {
    const { role, members } = props;
    const isEditAdmin = role?.type === RbacRoleType.Admin;
    const getLabel = (userItem: IUserListItem | ISupportUserModel) => {
      if (!userItem.UserName) {
        return userItem.UserID;
      }

      const isSuper = owner.userList.includes(userItem.UserID);
      const isAdmin = admin.userList.includes(userItem.UserID);
      const disabled =
        (isSuper || (isAdmin && !isEditAdmin)) && !isNoticeAdminRole;
      return (
        <div
          className={classNames(styles.userLabel, {
            [styles.disabled]: disabled,
          })}
        >
          <span>{userItem.UserName}</span>
          {isSuper && <CommonTag>Super Admin</CommonTag>}
          {isAdmin && !isEditAdmin && <CommonTag>Admin</CommonTag>}
          <span>{userItem.UserID}</span>
        </div>
      );
    };

    const allRoleMembers = [
      ...admin.userList,
      ...commonUsers,
      ...owner.userList,
    ];

    const noRoleMembers = members
      .filter((member) => !allRoleMembers.includes(member.UserID))
      .map((member) => member.UserID);
    let showedUsers = isEditAdmin
      ? [...noRoleMembers, ...allRoleMembers]
      : [
          ...noRoleMembers,
          ...commonUsers,
          ...admin.userList,
          ...owner.userList,
        ];
    if (isNoticeAdminRole) {
      showedUsers = [...owner.userList, ...admin.userList, ...commonUsers];
    }

    const options = showedUsers
      .map((userId) => {
        const matchedUser =
          allUser.find((item) => item.UserID === userId) ||
          members.find((item) => item.UserID === userId);
        if (!matchedUser) {
          return { label: '', value: '' };
        }
        const disabled =
          !isNoticeAdminRole &&
          (isEditAdmin
            ? owner.userList.includes(matchedUser.UserID)
            : [...admin.userList, ...owner.userList].includes(
                matchedUser.UserID
              ));

        return {
          label: getLabel(matchedUser),
          value: matchedUser.UserID,
          disabled,
        };
      })
      .filter((item) => Boolean(item.label));

    return options;
  };

  useEffect(() => {
    const { members } = props;
    if (!role) {
      return;
    }

    form.setFieldsValue({
      name: role.roleName,
      desc: role.desc,
      permissions: [...(role.permissions || [])].filter(
        (key) => key !== RbacAdminPermObjectKey.OrganizationRoleManage
      ),
      users: [...role.userList].filter((userId) =>
        members.some((item) => item.UserID === userId)
      ),
    });
  }, [role]);

  const onOk = async () => {
    if (!userInfo) {
      return false;
    }
    const infos = await form.validateFields();
    infos.permissions = uniq([
      ...(infos.permissions || []),
      RbacAdminPermObjectKey.OrganizationRoleManage,
    ]);
    let res = false;
    if (role) {
      res = await editSupportRole({
        ...role,
        roleName: infos.name,
        desc: infos.desc,
        permissions: infos.permissions,
        userList: infos.users,
        operator: userInfo.UserID,
      });
    } else {
      res = await addSupportRole({
        projectId: '0',
        creator: userInfo.UserID,
        roleName: infos.name,
        desc: infos.desc,
        type: RbacRoleType.Custom,
        permissions: infos.permissions,
        userList: infos.users,
      });
    }
    if (role?.type === RbacRoleType.Admin) {
      const { commonRoles } = props;
      await washRoleUsers(role, infos.users, [...commonRoles], userInfo.UserID);
    }
    if (!res) {
      messageError(getFormatMessage('operate_fail'));
      return false;
    }
    onSave();
    return true;
  };

  const renderInfo = () => {
    if (!viewOnly || !role) {
      return null;
    }
    return (
      <ConsoleForm form={form}>
        <Form.Item label={getFormatMessage('description')} name="desc">
          <span>{role.desc}</span>
        </Form.Item>
        {!isNoticeAdminRole && (
          <Form.Item
            label={getFormatMessage('access_rights')}
            name="permissions"
          >
            <SupportAccessRights roleType={role.type} viewOnly={true} />
          </Form.Item>
        )}
      </ConsoleForm>
    );
  };

  const renderForm = () => {
    if (viewOnly) {
      return null;
    }
    return (
      <ConsoleForm form={form}>
        <Form.Item
          label={getFormatMessage('role_name')}
          name="name"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input
            disabled={role?.type === RbacRoleType.Admin}
            placeholder={getFormatMessage('enter_name_of_role')}
          />
        </Form.Item>

        <Form.Item label={getFormatMessage('description')} name="desc">
          <Input.TextArea
            placeholder={getFormatMessage('enter_the_role_description')}
            style={{ minHeight: '66px' }}
          />
        </Form.Item>
        {!isNoticeAdminRole && (
          <Form.Item
            label={getFormatMessage('access_rights')}
            name="permissions"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <SupportAccessRights
              roleType={role?.type || RbacRoleType.Custom}
              viewOnly={
                role?.type === RbacRoleType.Admin ||
                role?.type === RbacRoleType.Owner
              }
            />
          </Form.Item>
        )}
        <Form.Item label={getFormatMessage('member')} name="users">
          <CommonSelect
            placeholder={getFormatMessage('please_select_members')}
            options={getUserOptions()}
            showSearch={true}
            mode="multiple"
          />
        </Form.Item>
      </ConsoleForm>
    );
  };

  const getTitle = () => {
    if (viewOnly) {
      return '查看角色';
    }
    return role
      ? getFormatMessage('edit_role')
      : getFormatMessage('add_new_role');
  };

  return (
    <ConsoleDrawer
      title={getTitle()}
      onOk={onOk}
      okText={getFormatMessage('submit')}
      hideOkButton={viewOnly}
      cancelText={viewOnly ? getFormatMessage('close') : undefined}
      onClose={onCancel}
      width={870}
    >
      {renderForm()}
      {renderInfo()}
    </ConsoleDrawer>
  );
};
